import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import * as turf from '@turf/turf';

import { DatePicker, Form, Input } from 'antd';
import { Button } from '@cropler/ui-kit';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

import styles from './styles.module.scss';

import { CropsSelect } from '../CropsSelect/CropsSelect.jsx';
import { Notification } from '../Notification/Notification.jsx';
import { mapBoxStyles, MapLat, MapLng, APIVersion } from '../../constants/keys';
import { DateFormat, DateLlFormat } from '../../constants/data';
import {
  fieldsApi,
  useCreateBoundariesMutation,
  useCreateSeasonMutation,
  useEditBoundariesMutation,
  useEditSeasonMutation,
  useGetBoundariesIdQuery,
} from '../../store/api/fields';
import { resetSelectedSeason, selectTypeBoundaries } from '../../store/slices/fields';
import { GetDisabledDates } from './season.lib.jsx';
import { useLocalStorage, useViewport } from '../../hooks';
import { SidebarHeader, Sidebar, SidebarBody } from '../Sidebar/index.jsx';
import { BreakpointMobile, BreakpointMobileUp } from '../../constants/ui';
import { changeZoom } from '../../store/slices/ui';
import { selectMainSensorPeriod } from '../../store/slices/sensors';
import { getSidebarLinksMap } from '../../utils/ui';
import { Icon as CroplerIcon } from '@cropler/ui-kit';

dayjs.extend(isBetween);
const baseUrl = `https://backend.services.cropler.io`;
// const baseUrl = `${import.meta.env.VITE_API_URL}`;

export const Season = ({ t }) => {
  const SidebarLinksMap = getSidebarLinksMap(t);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { widthViewport } = useViewport();

  const [mapLng, ] = useLocalStorage(MapLng, '');
  const [mapLat, ] = useLocalStorage(MapLat, '');
  const lng = 21.41524;
  const lat = 53.5784;

  const [form] = Form.useForm();
  const mapContainer = useRef(null);
  const map = useRef(null);
  const draw = useRef(null);
  const { zoom } = useSelector((state) => state.ui);
  const [currentZoom] = useState(zoom);

  const [selectedPolygon, setSelectedPolygon] = useState('');
  const [polygonCoordinates, setPolygonCoordinates] = useState([]);
  const [activeNotification, setActiveNotification] = useState(false);
  const [notification, setNotification] = useState('');
  const [createdBoundaryId, setCreatedBoundaryId] = useState('');
  const [formError, setFormError] = useState('');
  const [mapMode, setMapMode] = useState('');

  const { selectedField, selectedSeason, selectedSeasonsDates, isVarda } = useSelector((state) => state.fields);
  const { crops } = useSelector((state) => state.catalog);
  const { selectedSensorPeriod, addingSeason } = useSelector((state) => state.sensors);

  const [
    createBoundaries,
    {
      isSuccess: isCreateBoundariesSuccess,
      data: createdBoundary,
      isError: isCreateBoundaryError,
      error: createBoundaryError,
    },
  ] = useCreateBoundariesMutation();
  const [createSeason, { isSuccess: isCreateSeasonSuccess, isError: isCreateSeasonError, error: createSeasonError }] =
    useCreateSeasonMutation();
  const [editSeason, { isSuccess: isEditSeasonSuccess, isError: isEditSeasonError, error: editSeasonError }] =
    useEditSeasonMutation();
  const [
    editBoundaries,
    { isSuccess: isEditBoundariesSuccess, isError: isEditBoundaryError, error: editBoundaryError },
  ] = useEditBoundariesMutation();
  const { data: dataBoundary, isSuccess: isBoundarySuccess } = useGetBoundariesIdQuery(
    { id: selectedSeason.boundaryId },
    { skip: !selectedSeason.boundaryId, refetchOnMountOrArgChange: true },
  );

  const sowingDate = selectedSeason && selectedSeason.sowingDate ?
    dayjs(selectedSeason.sowingDate) :
    addingSeason && selectedSensorPeriod && selectedSensorPeriod.length > 0 ? dayjs(selectedSensorPeriod[0]) : null;

  const harvestingDate = selectedSeason && selectedSeason.harvestDate ?
    dayjs(selectedSeason.harvestDate) :
    addingSeason && selectedSensorPeriod && selectedSensorPeriod.length > 0 ? dayjs(selectedSensorPeriod[1]) : null;

  const initialFormData = {
    crop: selectedSeason ? (selectedSeason.crop ? selectedSeason.crop : crops && crops.length ? crops[0].id : '') : '',
    hybrid: (selectedSeason && selectedSeason.variety) ?? '',
    sowingDate,
    harvestingDate,
  };

  const disabledDates = GetDisabledDates(selectedSeasonsDates);
  const disabledSowingDate = (current) => {
    if (form.getFieldValue('harvestingDate')) {
      const harvestDate = dayjs(form.getFieldValue('harvestingDate'));
      return current.isAfter(harvestDate) || disabledDates.includes(current.format(DateFormat));
    }
    return disabledDates.includes(current.format(DateFormat));
  };
  const disabledHarvestDate = (current) => {
    if (form.getFieldValue('sowingDate')) {
      const sowingDate = dayjs(form.getFieldValue('sowingDate'));
      return current.isBefore(sowingDate) || disabledDates.includes(current.format(DateFormat));
    }
    return disabledDates.includes(current.format(DateFormat));
  };
  const checkDatesConflict = ({ sowingDate, harvestDate }) => {
    return selectedSeasonsDates.some((range) => {
      const rangeStart = dayjs(range.sowingDate);
      const rangeEnd = dayjs(range.harvestDate);
      return (
        rangeStart.isBetween(sowingDate, harvestDate, 'day', '[]') ||
        rangeEnd.isBetween(sowingDate, harvestDate, 'day', '[]')
      );
    });
  };

  const onChangeFilterCrop = (value) => {
    form.setFieldValue('crop', value.id);
  };

  const addSeason = ({ boundaryId }) => {
    const params = {
      boundaryId: boundaryId,
      fieldId: selectedField,
      cropId: form.getFieldValue('crop'),
      variety: form.getFieldValue('hybrid'),
      sowingDate: dayjs(form.getFieldValue('sowingDate')).format(DateFormat),
      harvestDate: dayjs(form.getFieldValue('harvestingDate')).format(DateFormat),
    };
    createSeason(params);
  };

  const onCancel = () => {
    dispatch(resetSelectedSeason());
    dispatch(selectTypeBoundaries(false));
    navigate(-1);
  };

  const onSaveSeason = () => {
    setFormError('');
    form.validateFields().then(() => {
      const sowingDate = form.getFieldValue('sowingDate');
      const harvestDate = form.getFieldValue('harvestingDate');
      if (harvestDate && checkDatesConflict({ sowingDate, harvestDate })) {
        setNotification(t('fields.seasonForm.messageDatesConflict'));
        setActiveNotification(true);
      } else {
        if (polygonCoordinates.length) {
          setActiveNotification(false);

          if (!selectedSeason.id) {
            // add season
            if (!createdBoundaryId) {
              createBoundaries({ coordinates: isVarda ? [polygonCoordinates] : polygonCoordinates });
            } else {
              addSeason({ boundaryId: createdBoundaryId });
            }
          } else {
            // edit season
            const params = {
              id: selectedSeason.id,
              boundaryId: selectedSeason.boundaryId,
              fieldId: selectedField,
              cropId: form.getFieldValue('crop'),
              variety: form.getFieldValue('hybrid'),
              sowingDate: dayjs(sowingDate).format(DateFormat),
              harvestDate: harvestDate ? dayjs(harvestDate).format(DateFormat) : '',
            };
            editSeason(params);
          }
        } else {
          setNotification(t('fields.seasonForm.messageBoundaries'));
          setActiveNotification(true);
        }
      }
    });
  };

  const onCutPolygon = () => {
    if (!draw.current || !selectedPolygon) {
      setNotification(t('fields.seasonForm.messageDrawPolygon'));
      setActiveNotification(true);
      return;
    }

    const drawnFeatures = draw.current.getAll();

    const mainPolygon = turf.polygon(drawnFeatures.features[0].geometry.coordinates);
    const userPolygonCoordinates = drawnFeatures.features.filter((obj) => obj.id === selectedPolygon);
    const userPolygon = turf.polygon(userPolygonCoordinates[0].geometry.coordinates);

    // Check if the selected polygon is nested in the main one
    const isInside = turf.booleanContains(mainPolygon, userPolygon);
    if (!isInside) {
      setNotification(t('fields.seasonForm.errorPolygon'));
      setActiveNotification(true);
    }

    // Cut the selected polygon in the main one
    const cutPolygon = turf.difference(turf.featureCollection([mainPolygon, userPolygon]));
    if (!cutPolygon) {
      setNotification(t('fields.seasonForm.errorCuttingPolygon'));
      setActiveNotification(true);
    }

    setPolygonCoordinates(cutPolygon.geometry.coordinates);
    draw.current.deleteAll();
    draw.current.add(cutPolygon);
  };

  const setDirectSelectMode = () => {
    const allFeatures = draw.current.getAll();
    if (allFeatures && allFeatures.features.length > 0) {
      if (mapMode !== 'direct_select') {
        setMapMode('direct_select');
        allFeatures.features.map((feature) => {
          draw.current.changeMode('direct_select', { featureId: feature.id });
        });
      } else {
        setMapMode('simple_select');
        draw.current.changeMode('simple_select');
      }
    }
  };

  // Init map
  useEffect(() => {
    if (!selectedField) {
      dispatch(changeZoom(currentZoom));
      navigate('/fields');
    }

    if (map.current) return; // initialize map only once

    if (isVarda) {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: mapBoxStyles,
        center: mapLng && mapLat ? [mapLng, mapLat] : [lng, lat],
        zoom: 14.5,
        hash: true,
        attributionControl: false,
      }).addControl(
        new mapboxgl.AttributionControl({
          customAttribution: '<a target="_blank" href="https://varda.ag/">Powered by Varda</a>',
        }),
      );
      map.current.on('load', () => {
        map.current.addSource('boundaries-tiles', {
          type: 'vector',
          tiles: [`${baseUrl}/data-providers-api/${APIVersion}/boundaries/tiles/{z}/{x}/{y}`],
          minzoom: 0,
          maxzoom: 12,
          promoteId: 'id',
        });

        map.current.addLayer({
          id: 'boundaries-layer-fill',
          type: 'fill',
          source: 'boundaries-tiles',
          'source-layer': 'boundaries',
          paint: {
            'fill-color': [
              'case',
              ['boolean', ['feature-state', 'clicked'], false],
              '#32D583',
              ['boolean', ['feature-state', 'hover'], false],
              '#efc336',
              'rgb(222, 236, 220)',
            ],
            'fill-opacity': 0.6,
          },
        });

        map.current.addLayer({
          id: 'boundaries-layer-outline',
          type: 'line',
          source: 'boundaries-tiles',
          'source-layer': 'boundaries',
          paint: {
            'line-color': ['case', ['boolean', ['feature-state', 'clicked'], false], '#32D583', '#fff'],
            'line-width': 2,
          },
        });

        let hoveredPolygonId = null;
        let clickedPolygonId = null;

        // Hover event on polygon
        map.current.on('mousemove', 'boundaries-layer-fill', (e) => {
          if (e.features.length > 0) {
            if (hoveredPolygonId !== null) {
              map.current.setFeatureState(
                { source: 'boundaries-tiles', sourceLayer: 'boundaries', id: hoveredPolygonId },
                { hover: false },
              );
            }
            hoveredPolygonId = e.features[0].id;
            map.current.setFeatureState(
              { source: 'boundaries-tiles', sourceLayer: 'boundaries', id: hoveredPolygonId },
              { hover: true },
            );
          }
        });

        map.current.on('mouseenter', 'boundaries-layer-fill', () => {
          map.current.getCanvas().style.cursor = 'pointer';
        });

        map.current.on('mouseleave', 'boundaries-layer-fill', () => {
          map.current.getCanvas().style.cursor = '';

          if (hoveredPolygonId !== null) {
            map.current.setFeatureState(
              { source: 'boundaries-tiles', sourceLayer: 'boundaries', id: hoveredPolygonId },
              { hover: false },
            );
          }
          hoveredPolygonId = null;
        });

        // Select polygon on click
        map.current.on('click', 'boundaries-layer-fill', (e) => {
          if (e.features.length > 0) {
            setPolygonCoordinates(e.features[0].geometry.coordinates);

            if (clickedPolygonId) {
              map.current.setFeatureState(
                { source: 'boundaries-tiles', sourceLayer: 'boundaries', id: clickedPolygonId },
                { clicked: false },
              );
            }

            clickedPolygonId = e.features[0].id;
            map.current.setFeatureState(
              { source: 'boundaries-tiles', sourceLayer: 'boundaries', id: clickedPolygonId },
              { clicked: true },
            );
          }
        });
      });
    } else {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: mapBoxStyles,
        center: mapLng && mapLat ? [mapLng, mapLat] : [lng, lat],
        zoom: currentZoom,
        hash: true,
      }).addControl(new mapboxgl.NavigationControl());

      draw.current = new MapboxDraw({
        displayControlsDefault: false,
        // controls: {
        //   //point, line_string, polygon, trash, combine_features and uncombine_features
        // },
        defaultMode: 'draw_polygon', //simple_select, draw_polygon, direct_select
      });

      map.current.addControl(draw.current);

      const updateArea = () => {
        const data = draw.current.getAll();
        const currentCoordinates = [];
        if (data.features.length > 0) {
          data.features.map((obj) => {
            currentCoordinates.push(obj.geometry.coordinates);
          });
        }
        setPolygonCoordinates(currentCoordinates);
      };

      map.current.on('draw.create', updateArea);
      map.current.on('draw.delete', updateArea);
      map.current.on('draw.update', updateArea);

      map.current.on('draw.selectionchange', (e) => {
        if (e.features.length > 0) {
          const selectedFeature = e.features[0];
          setSelectedPolygon(selectedFeature.id);
        } else {
          setSelectedPolygon('');
        }
      });

      if ((draw.current && selectedSeason.id) || selectedSeason.boundaryId) {
        draw.current.changeMode('simple_select');
      }

      // after editing, drag pan stops working
      map.current.on('draw.modechange', (e) => {
        setMapMode(e.mode);
        if (e.mode === 'direct_select') {
          map.current.touchZoomRotate.enable();
          map.current.dragPan.enable();
        }
      });
    }
  }, []);

  // Create boundary
  useEffect(() => {
    if (isCreateBoundariesSuccess && createdBoundary && !isCreateSeasonSuccess) {
      setCreatedBoundaryId(createdBoundary.id);
      addSeason({ boundaryId: createdBoundary.id });
    }
  }, [isCreateBoundariesSuccess]);

  // Go to Fields screen
  useEffect(() => {
    if (isCreateSeasonSuccess || isEditBoundariesSuccess) {
      dispatch(fieldsApi.util.resetApiState());
      dispatch(resetSelectedSeason());
      dispatch(selectTypeBoundaries(false));
      dispatch(changeZoom(currentZoom));
      dispatch(selectMainSensorPeriod({ period: [] }));
      navigate('/fields');
    }
  }, [isCreateSeasonSuccess, isEditBoundariesSuccess]);

  // Edit boundaries
  useEffect(() => {
    if (isEditSeasonSuccess) {
      editBoundaries({
        id: selectedSeason.boundaryId,
        coordinates: polygonCoordinates,
      });
    }
  }, [isEditSeasonSuccess]);

  // Editable polygon
  useEffect(() => {
    if (isBoundarySuccess && dataBoundary && map.current && draw.current) {
      const coordinates = dataBoundary.geom.coordinates[0];
      const polygonFeature = {
        id: 'editable-polygon',
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'Polygon',
          coordinates: coordinates,
        },
      };

      draw.current.add(polygonFeature);

      // Center the map around the polygon
      const bounds = coordinates[0].reduce(
        (bounds, coord) => bounds.extend(coord),
        new mapboxgl.LngLatBounds(coordinates[0][0], coordinates[0][0]),
      );

      map.current.fitBounds(bounds, { padding: widthViewport > BreakpointMobile ? 240 : 80 });

      setPolygonCoordinates([coordinates]);
    }
  }, [isBoundarySuccess, dataBoundary, map.current]);

  // Errors
  useEffect(() => {
    if (isCreateBoundaryError && createBoundaryError) {
      setNotification(createBoundaryError[0].detail);
      setActiveNotification(true);
    } else if (isEditBoundaryError && editBoundaryError) {
      setNotification(editBoundaryError[0].detail);
      setActiveNotification(true);
    }
  }, [isCreateBoundaryError, isEditBoundaryError, createBoundaryError, editBoundaryError]);

  // Errors
  useEffect(() => {
    if (isCreateSeasonError && createSeasonError) {
      setFormError(createSeasonError[0].detail);
    } else if (isEditSeasonError && editSeasonError) {
      setNotification(editSeasonError[0].detail);
    } else {
      setFormError('');
    }
  }, [isCreateSeasonError, isEditSeasonError, editSeasonError, createSeasonError]);

  return (
    <>
      <div className="workspace">
        <Sidebar isSeasonPage={true}>
          {/*Mobile View*/}
          {widthViewport < BreakpointMobileUp && (
            <SidebarHeader
              title={selectedSeason.id ? SidebarLinksMap.season.titleSecondary : SidebarLinksMap.season.title}
              text={
                selectedSeason.id
                  ? SidebarLinksMap.season.descriptionEdit
                  : isVarda
                    ? SidebarLinksMap.season.descriptionAddVarda
                    : SidebarLinksMap.season.descriptionAdd
              }
            />
          )}
          <SidebarBody>
            <Form
              className={`${styles.form} custom-scroll`}
              form={form}
              layout="vertical"
              initialValues={initialFormData}
              autoComplete="off"
              requiredMark={false}
            >
              <Form.Item
                label={t('fields.seasonForm.crop')}
                name="crop"
                rules={[
                  {
                    required: true,
                    message: t('fields.seasonForm.cropPlaceholder'),
                  },
                ]}
              >
                <CropsSelect
                  key={initialFormData.crop}
                  changeCrop={onChangeFilterCrop}
                  currentCrop={initialFormData.crop}
                  size={'large'}
                  t={t}
                />
              </Form.Item>
              <Form.Item label={t('fields.seasonForm.hybrid')} name="hybrid">
                <Input placeholder={t('fields.seasonForm.hybridPlaceholder')} size={'large'} />
              </Form.Item>
              <Form.Item
                label={t('fields.seasonForm.seedingDate')}
                name="sowingDate"
                rules={[
                  {
                    required: true,
                    message: t('fields.seasonForm.seedingDatePlaceholder'),
                  },
                ]}
              >
                <DatePicker
                  format={DateLlFormat}
                  disabledDate={disabledSowingDate}
                  suffixIcon={<CroplerIcon name="calendar" />}
                  placement="top-left"
                  rootClassName={'custom-date-picker'}
                  size={'large'}
                />
              </Form.Item>
              <Form.Item label={t('fields.seasonForm.harvestingDate')} name="harvestingDate">
                <DatePicker
                  format={DateLlFormat}
                  disabledDate={disabledHarvestDate}
                  suffixIcon={<CroplerIcon name="calendar" />}
                  placement="top-left"
                  rootClassName={'custom-date-picker'}
                  size={'large'}
                />
              </Form.Item>
              {formError && <div className={styles.formErrors}>{formError}</div>}
              {widthViewport > BreakpointMobile && (
                <div className={styles.formFooter}>
                  <Button size={'large'} type="secondary" onClick={onCancel}>
                    {t('actions.cancel')}
                  </Button>
                  <Button size={'large'} type="primary" htmlType="submit" onClick={onSaveSeason}>
                    {t('fields.buttonSaveSeason')}
                  </Button>
                </div>
              )}
            </Form>
          </SidebarBody>
        </Sidebar>
        {widthViewport < BreakpointMobileUp && (
          <div className={styles.formFooter}>
            <Button type="secondary" onClick={onCancel} size={'large'}>
              {t('actions.cancel')}
            </Button>
            <Button type="primary" onClick={onSaveSeason} size={'large'}>
              {t('fields.buttonSaveSeason')}
            </Button>
          </div>
        )}
        <div ref={mapContainer} className="workspace-body map-season"></div>
        <div className={styles.mapDrawNav}>
          <Button
            className={`${mapMode === 'direct_select' ? styles.mapDrawNavBtnActive : ''}`}
            type="default"
            icon={<CroplerIcon name="edit" />}
            onClick={() => setDirectSelectMode()}
          />
          <Button type="default" icon={<CroplerIcon name="polygon" />} onClick={() => draw.current.changeMode('draw_polygon')} />
          <Button type="default" icon={<CroplerIcon name="cut" />} onClick={onCutPolygon} />
          <div className={styles.mapDrawNavBorder}></div>
          <Button type="default" icon={<CroplerIcon name="remove" />} onClick={() => draw.current.trash()} />
        </div>
        {activeNotification && (
          <div className={styles.mapNotification}>
            <Notification message={notification} close={() => setActiveNotification(false)} />
          </div>
        )}
      </div>
    </>
  );
};
